import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

import { authStore } from "./auth";
import type { AdminSession, AuthState, AuthStore } from "./auth-store";

interface AuthContextValue {
  state: AuthState;
  /** The signed-in admin, or null. Inside the dashboard it's always set (the route requires it). */
  session: AdminSession | null;
  signIn: AuthStore["signIn"];
  signOut: AuthStore["signOut"];
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Where to go once signed out. An ended session brings the admin back to this page after signing
 * in again; a deliberate sign-out starts fresh, so the next person doesn't land on this screen.
 */
function loginUrl(pathname: string, search: string, expired: boolean) {
  const here = `${pathname}${search}`;
  if (!expired) return "/login";
  const params = new URLSearchParams({ reason: "expired" });
  if (here !== "/") params.set("next", here);
  return `/login?${params.toString()}`;
}

/**
 * Puts the auth store into React. When the session ends — signed out here or in another tab,
 * expired, or refused by the API — it forgets the previous admin's cached data and opens the
 * sign-in page.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const state = useSyncExternalStore(authStore.subscribe, authStore.getState);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const wasSignedIn = useRef(state.status === "signed-in");

  useEffect(() => {
    const signedIn = state.status === "signed-in";
    if (wasSignedIn.current && !signedIn) {
      // Never leave one admin's orders and customers in memory for whoever signs in next.
      queryClient.clear();
      if (pathname !== "/login") {
        void navigate(loginUrl(pathname, search, state.reason === "expired"), { replace: true });
      }
    }
    wasSignedIn.current = signedIn;
  }, [state, pathname, search, navigate, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      session: state.status === "signed-in" ? state.session : null,
      signIn: authStore.signIn,
      signOut: authStore.signOut,
    }),
    [state],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error("useAuth must be used inside AuthProvider (the router's root route)");
  return auth;
}

/** The signed-in admin, for screens inside the dashboard (its route guarantees one). */
export function useSession(): AdminSession {
  const { session } = useAuth();
  if (!session) throw new Error("useSession is only for screens behind the dashboard's sign-in");
  return session;
}
